import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaUserSlash, FaUnlock, FaMapMarkerAlt } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";

const BlockedUser = () => {
  const navigate = useNavigate();
  const [blockedList, setBlockedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Initialize Auth and Fetch List
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      const myId = decoded.id || decoded._id;
      setUserId(myId);
      fetchBlockedUsers(myId);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchBlockedUsers = async (myId) => {
    try {
      setLoading(true);
      const res = await api.getBlockedUsers(myId);
      if (res?.data?.success) {
        setBlockedList(res.data.blockedSellers);
      }
    } catch (err) {
      console.error("Error fetching blocked users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (sellerId, sellerName) => {
    const confirmUnblock = window.confirm(
      `Are you sure you want to unblock ${sellerName}? They will be able to see your profile and you will see their products again.`
    );

    if (confirmUnblock) {
      try {
        const res = await api.unblockUser({ userId, sellerId });
        if (res?.data?.success) {
          // Update UI locally immediately
          setBlockedList((prev) => prev.filter((item) => item._id !== sellerId));
        }
      } catch (err) {
        alert("Failed to unblock user.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-6 py-4 border-b border-gray-100 shadow-sm">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <FaArrowLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-black text-gray-900">Blocked Sellers</h1>
        <div className="w-10" />
      </div>

      <div className="max-w-2xl mx-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-500 font-bold">Loading blocked list...</p>
          </div>
        ) : blockedList.length > 0 ? (
          <div className="space-y-4">
            {blockedList.map((user) => (
              <div 
                key={user._id} 
                className="bg-white p-4 rounded-3xl shadow-sm border border-gray-100 flex items-center justify-between hover:border-teal-100 transition-all"
              >
                <div className="flex items-center space-x-4">
                  <img 
                    src={user.profileImage || "/default-avatar.png"} 
                    alt={user.Name} 
                    className="w-14 h-14 rounded-2xl object-cover bg-gray-50"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">{user.Name}</h3>
                    <div className="flex items-center text-gray-400 text-xs mt-1">
                      <FaMapMarkerAlt className="mr-1" />
                      <span>{user.lga || "Unknown Location"}</span>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => handleUnblock(user._id, user.Name)}
                  className="flex items-center space-x-2 bg-teal-50 text-teal-600 px-4 py-2 rounded-xl border border-teal-100 hover:bg-teal-600 hover:text-white transition-all font-bold text-sm"
                >
                  <FaUnlock size={14} />
                  <span>Unblock</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="bg-white p-8 rounded-full shadow-inner mb-6">
              <FaUserSlash size={64} className="text-gray-200" />
            </div>
            <h2 className="text-xl font-black text-gray-700">No Blocked Sellers</h2>
            <p className="text-gray-400 mt-2 max-w-xs font-medium">
              Sellers you block will appear here. You can manage them at any time.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedUser;